import { useLayoutEffect, useRef, useState } from 'react';
// Import Styles
import styles from './scrollingText.module.scss';

type Props = {
    children: string;
    speed?: number; // px per second
};

const ScrollingText: React.FC<Props> = ({ children, speed = 50 }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);

    const [shouldScroll, setShouldScroll] = useState(false);
    const [distance, setDistance] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const text = textRef.current;

        if (!container || !text) return;

        const containerWidth = container.offsetWidth;

        const firstBlock = text.children[0] as HTMLElement;
        if (!firstBlock) return;

        const styles = getComputedStyle(text);
        const gap = parseFloat(styles.gap || '0');

        const blockWidth = firstBlock.offsetWidth + gap;

        if (firstBlock.offsetWidth > containerWidth) {
            const finalDistance = Math.round(blockWidth);

            setShouldScroll(true);
            setDistance(finalDistance);
        } else {
            setShouldScroll(false);
        }
    }, [children]);

    const duration = distance / speed;

    return (
        <div
            ref={containerRef}
            className={styles.scrollableContainer}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <span
                ref={textRef}
                className={`${styles.scrollableInner} ${shouldScroll ? styles.scrollableInnerAnimated : ''}`}
                style={
                    {
                        '--scroll-duration': `${duration}s`,
                        '--scroll-distance': `-${distance}px`,
                        animationPlayState: isPaused ? 'paused' : 'running',
                    } as React.CSSProperties
                }
            >
                <span className={styles.block}>{children}</span>
                {shouldScroll && <span className={styles.block}>{children}</span>}
            </span>
        </div>
    );
};

export default ScrollingText;
